import * as mobile from "./mobile";
import {
	IntegrationCredentialsEvents,
	subscribe as subscribeToIntegrationCredentialsEvent
} from "client/Features/Settings/IntegrationCredentials/events";
import { LinkConfiguration } from "client/Api/IntegrationCredentials/integrationCredentials";
import { ExchangeServerInstanceType } from "client/Api/Email/ExchangeSettings/environmentExchangeSettings";
import { ServiceHostingLocation } from "client/Api/SingleSignOnSettings/singleSignOnSettings";
import { Guid } from "client_legacy/ndaw/guid";
import { DestinationWindow } from "client/Api/Apps/common";
import { UserIdentityModel } from "client/Api/UserProfile/service";
import { UserLicense } from "client/Api/FeaturePackages/featurePackageManagement";
import { OfficeDocumentEditor } from "client/Api/Documents/common";
import { OfficeWebAppOpenTarget } from "client/Api/Documents/SharePoint/common";
import { ConfigurableAddressBookAppContactSource } from "client/Api/Apps/EditApp/saveApp";
import { IntercomRegion } from "client/Api/Intercom/intercomConfiguration";

interface DocumentsConfiguration {
	sharePoint: SharePointConfiguration;
	webDav: WebDavConfiguration | null;
	allowCrossStoreOperations: boolean;
}

export type SharePointConfiguration = {
	localApp: LocalAppConfiguration;
	defaultOfficeDocumentEditor: OfficeDocumentEditor;
	wopi: SharePointWopiConfiguration;
	pageSize: number;
	foldersAndFilesGridBusyIndicatorDelay: number;
	isSharingViaLinkEnabled: boolean;
	isSharePointViewerEnabled: boolean;
	doesSupportThumbnails: boolean;
} & (
		{
			isConfigured: true;
			rootUrl: string;
		}
		|
		{
			isConfigured: false;
			rootUrl: null;
		}
	);

interface SharePointWopiConfiguration {
	openTarget: OfficeWebAppOpenTarget;
	isVisioSupported: boolean;
	isOneNoteSupported: boolean;
}

interface LocalAppConfiguration {
	isEnabled: boolean;
}

export interface WebDavConfiguration {
	isConfigured: boolean;
	defaultOfficeDocumentEditor: OfficeDocumentEditor;
	localApp: LocalAppConfiguration;
	wopi: WebDavWopiConfiguration;
	illegalCharacters: string[];
}

interface WebDavWopiConfiguration {
	isEnabled: boolean;
	openTarget: OfficeWebAppOpenTarget;
}

let _documentsConfiguration: DocumentsConfiguration = null!;
export function initializeDocumentsConfiguration(configuration: DocumentsConfiguration) {
	_documentsConfiguration = configuration;
}

// We export it as a "provider" as at the time of exporting, the reference to the configuration is not set
export function documentsConfiguration(): DocumentsConfiguration {
	return _documentsConfiguration;
}

export enum TermsNoticeKind {
	None = 0,
	UpdatedTerms = 1,
	Terms = 2
}

export type ClientUserConfiguration = {
	// ProductVersion is not used by our product but it is used for 3rd party metrics collection
	productVersion: string;
	instanceLevelProductName: string;
	activeDirectoryHostingLocation: ServiceHostingLocation;
	/**
	 * @deprecated Use currentUserIdentity instead. We still have userId here for pendo scripts
	 */
	userId: number;
	currentUserIdentity: UserIdentityModel;
	emailAddress: string | null;
	isAdmin: boolean;
	integrationCredentialsConfiguredLinks: LinkConfiguration;
	exchange: ClientExchangeConfiguration;
	email: ClientEmailConfiguration;
	calendar: CalendarClientConfiguration;
	notificationsListUpdateIntervalInSeconds: number;
	isHubAppAvailable: boolean;
	databaseConfiguration: ClientUserDatabaseConfiguration;
	errorPages: ClientErrorPageConfiguration;
	document: DocumentClientConfiguration;
	branding: ClientBrandingConfiguration;
	timeZone: ClientTimeZoneConfiguration | null;
	userLicense: UserLicense;
	userHtml: UserHtmlModel;
	popupQueueUpdateIntervalInSeconds: number;
	sibi: SibiUserIntegrationModel | null;
	intercom: IntercomIntegrationModel | null;
	addressBook: AddressBookClientConfiguration;
	aiAssistant: AiAssistantConfiguration;
	termsNotice: TermsNoticeKind;
};

type AiAssistantConfiguration =
	| {
		isAiAssistantAvailable: true;
		kelly: KellyConfiguration;
	}
	| {
		isAiAssistantAvailable: false;
		kelly: null;
	};

export type KellyConfiguration = {
	tenantId: Guid;
	baseUrl: string;
};

type AddressBookClientConfiguration =
	| {
		isAppAvailable: true;
		configurableContactSources: readonly ConfigurableAddressBookAppContactSource[];
	}
	| {
		isAppAvailable: false;
		configurableContactSources: null;
	};

export enum EmailClientPreference {
	UseNone = 1,
	Workspace = 2,
	OutlookWebApp = 3
}

export type ClientExchangeConfiguration = {
	isEnabled: boolean;
	isConfigured: boolean;
	ewsUrl: string | null;
	totalAttachmentsSizeLimit: number;
	saveDraftsIntervalInSeconds: number;
	exchangeServerInstanceType: ExchangeServerInstanceType;
} & (
		{
			isConfigured: true;
			ewsUrl: string;
		}
		|
		{
			isConfigured: false;
			ewsUrl: null;
		}
	);

interface ClientEmailConfiguration {
	emailClientPreference: EmailClientPreference;
	destinationWindow: DestinationWindow;
}

export type DocumentClientConfiguration = {
	isAppAvailable: boolean;
	destinationWindow: DestinationWindow;
};

export enum CalendarClientPreference {
	UseNone = 1,
	Workspace = 2,
	OutlookWebApp = 3
}

export type CalendarClientConfiguration = {
	clientPreference: CalendarClientPreference;
	destinationWindow: DestinationWindow;
};

interface ClientUserDatabaseConfiguration {
	environmentId: Guid;
	encryptionPublic: string;
	encryptionKey: string;
	maxAmountOfNotificationsToStoreInFrontEnd: number;
}

export type ClientErrorPageConfiguration = {
	errorPageHtml: string;
	supportEmailAddress: string | null;
	supportTelephoneNumber: string | null;
};

export enum WorkspaceHeaderStyle {
	ColoredBackground = 0,
	WhiteBackground = 1
}

export type ClientBrandingConfiguration = {
	productName: string;
	workspaceHeaderStyle: WorkspaceHeaderStyle;
	homeLogoId: string | null;
	darkThemeHomeLogoId: string | null;
	customHomeButtonUrl: string | null;
};

export type ClientTimeZoneConfiguration = {
	identifier: string;
	utcOffsetInMinutes: number;
};

export type UserHtmlModel = { isConfigured: false; }
	| {
		isConfigured: true;
		url: string;
	};

export type SibiUserIntegrationModel = {
	subdomain: string;
	employeeReference: string;
};

export type IntercomIntegrationModel = {
	appId: string;
	region: IntercomRegion;
	userJwt: string | null;
};

let _userConfiguration: ClientUserConfiguration = null!;
let _userConfigurationPromiseHandlers: PromiseHandlers<ClientUserConfiguration>;
const _userConfigurationPromise: Promise<ClientUserConfiguration> = new Promise((resolve, reject) => {
	_userConfigurationPromiseHandlers = {resolve, reject};
});

export function initializeUserConfiguration(configuration: ClientUserConfiguration) {
	_userConfiguration = configuration;

	const { integrationCredentialsConfiguredLinks } = _userConfiguration;

	subscribeToIntegrationCredentialsEvent(
		IntegrationCredentialsEvents.ExchangeLinked,
		() => {
			integrationCredentialsConfiguredLinks.exchange = true;
		}
	);

	subscribeToIntegrationCredentialsEvent(
		IntegrationCredentialsEvents.FileServersLinked,
		() => {
			integrationCredentialsConfiguredLinks.fileServers = true;
		}
	);

	subscribeToIntegrationCredentialsEvent(
		IntegrationCredentialsEvents.EnvironmentAppLinked,
		(_, { id }: { id : number;}) => {
			integrationCredentialsConfiguredLinks.environmentApps = [
				...integrationCredentialsConfiguredLinks.environmentApps,
				id
			];
		}
	);

	subscribeToIntegrationCredentialsEvent(
		IntegrationCredentialsEvents.IntegrationFlowAuthenticationLinked,
		(_, { id }: { id : number;}) => {
			integrationCredentialsConfiguredLinks.integrationFlowAuthentications = [
				...integrationCredentialsConfiguredLinks.integrationFlowAuthentications,
				id
			];
		}
	);

	_userConfigurationPromiseHandlers.resolve(configuration);
}

// We export it as a "provider" as at the time of exporting, the reference to the configuration is not set
export function userConfiguration(): ClientUserConfiguration {
	return _userConfiguration;
}

export function waitForUserConfigurationToInitialize(): Promise<ClientUserConfiguration> {
	return _userConfigurationPromise;
}

export const deviceHelper = {
	wopi: {
		areAsyncRequestsAllowed: (openTarget: OfficeWebAppOpenTarget) => {
			//Safari doesn't allow to open new tab in "success" function of async ajax request
			return !mobile.isIpad() || openTarget === OfficeWebAppOpenTarget.Popup;
		},
		allowDragging: () => true,
		allowResizing: () => true,
		freezeDialog: {
			shouldShow: () => true
		}
	},
	localApp: {
		isSupported: () => true
	},
	sharePoint: {
		isSyncActionVisible: () => true,
	}
} as const;

export { isNewFeatureDialogEnabled } from "./observableSettings";
